/**
 * ARIES chat transcript persistence, per Case.
 *
 * Lets a Case abandoned mid-Creation be resumed with its prior conversation
 * still on screen. The backend has no replayable transcript to offer — a
 * `CaseChatMessage` stores the human side and `leave_detail` diffs but never
 * ARIES's own ISM text — so the rendered timeline is kept client-side.
 *
 * **Opt-in**: every function is a no-op unless the deployment enables
 * transcripts (see `config/featureFlags`). Resume itself does not depend on
 * this — the `continue-chat` trigger re-asks the outstanding question either
 * way; the transcript is purely what the Admin sees above it.
 *
 * `localStorage` (not `sessionStorage`, unlike `confirmationDraftStorage`)
 * because a resume has to survive closing the tab. That means it also
 * survives logout, which `hooks/useAuth`'s teardown only prefix-clears for
 * `ROCP_*` — hence {@link purgeAllChatHistory}, wired into that teardown.
 *
 * What is stored is already PHI-sanitized: `Chat.composer` routes typed text
 * through `POST /leave/chat-sanitize/` before it ever reaches the reducer, so
 * the timeline holds generalized text by construction. `leaveDetail` is
 * deliberately NOT stored — `Case.leave_detail` on the server is the single
 * source of truth for the accumulator, and a stale local copy could overwrite
 * good server state on the next turn.
 */

import { isChatTranscriptEnabled } from "../config/featureFlags";

import type { ChatParticipant, ChatTimelineItem, ConversationMeta } from "../types/chat";

const CHAT_HISTORY_STORAGE_PREFIX = "aries-chat-history:";

/** Bumped on a breaking envelope change; mismatches are discarded on read. */
const CHAT_HISTORY_VERSION = 1;

/** Transcripts older than this are treated as absent and evicted. */
const CHAT_HISTORY_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** The Creation-stage slice worth restoring — presentation only. */
export interface StoredChatHistory {
  timeline: ChatTimelineItem[];
  participants: Record<string, ChatParticipant>;
  conversation: ConversationMeta;
}

interface ChatHistoryEnvelope extends StoredChatHistory {
  v: number;
  savedAt: number;
}

function storageKey(caseId: string): string {
  return `${CHAT_HISTORY_STORAGE_PREFIX}${caseId}`;
}

/**
 * `JSON.stringify` turns every `Date` into an ISO string, so the parsed value
 * lies about its own type until these are revived. There are three of them in
 * the timeline union — `ChatMessageItem.timestamp`, `ChatDateDividerItem.date`
 * and `ConversationMeta.createdAt` — and missing any one leaves a `string`
 * where components call `Date` methods.
 */
function reviveTimeline(timeline: ChatTimelineItem[]): ChatTimelineItem[] {
  return timeline
    // A persisted "typing" indicator would rehydrate as a spinner that never
    // resolves, since the stream that produced it is long gone.
    .filter((item) => item.kind !== "typing")
    .map((item) => {
      if (item.kind === "message") {
        return { ...item, timestamp: new Date(item.timestamp) };
      }

      if (item.kind === "date-divider") {
        return { ...item, date: new Date(item.date) };
      }

      return item;
    });
}

/**
 * Persists the Creation-stage transcript for `caseId`. No-ops when the
 * feature is disabled, or when storage rejects the write (quota) — a failed
 * transcript save must never break the live conversation.
 */
export function writeChatHistory(caseId: string, history: StoredChatHistory): void {
  if (!isChatTranscriptEnabled()) {
    return;
  }

  try {
    const envelope: ChatHistoryEnvelope = {
      v: CHAT_HISTORY_VERSION,
      savedAt: Date.now(),
      ...history,
    };

    localStorage.setItem(storageKey(caseId), JSON.stringify(envelope));
  } catch (error) {
    console.warn("Unable to write ARIES chat history.", error);
  }
}

/**
 * Returns the stored transcript for `caseId`, or `undefined` when there is
 * none, the envelope version doesn't match, it has aged past the TTL, or the
 * feature is disabled.
 *
 * When disabled it also clears any key left behind by a previous deployment
 * that had transcripts on, so flipping the flag off cleans up rather than
 * leaving conversation content sitting on the device.
 */
export function readChatHistory(caseId: string): StoredChatHistory | undefined {
  if (!isChatTranscriptEnabled()) {
    clearChatHistory(caseId, { force: true });

    return undefined;
  }

  try {
    const raw = localStorage.getItem(storageKey(caseId));

    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as ChatHistoryEnvelope;

    if (parsed.v !== CHAT_HISTORY_VERSION || Date.now() - parsed.savedAt > CHAT_HISTORY_TTL_MS) {
      clearChatHistory(caseId, { force: true });

      return undefined;
    }

    return {
      timeline: reviveTimeline(parsed.timeline ?? []),
      participants: parsed.participants ?? {},
      conversation: {
        ...parsed.conversation,
        createdAt: new Date(parsed.conversation.createdAt),
      },
    };
  } catch (error) {
    console.warn("Unable to read ARIES chat history.", error);

    return undefined;
  }
}

/**
 * Removes the transcript for one Case. `force` bypasses the feature check so
 * cleanup still runs for a deployment that has since disabled transcripts.
 */
export function clearChatHistory(caseId: string, { force = false } = {}): void {
  if (!force && !isChatTranscriptEnabled()) {
    return;
  }

  try {
    localStorage.removeItem(storageKey(caseId));
  } catch (error) {
    console.warn("Unable to clear ARIES chat history.", error);
  }
}

/**
 * Removes every stored transcript. Wired into the logout teardown so
 * conversation content doesn't outlive the session on a shared machine —
 * unconditional by design, since the flag may have been on earlier.
 */
export function purgeAllChatHistory(): void {
  try {
    const keys = Object.keys(localStorage).filter((key) =>
      key.startsWith(CHAT_HISTORY_STORAGE_PREFIX),
    );

    keys.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn("Unable to purge ARIES chat history.", error);
  }
}
